import {FC, useEffect, useState, ReactNode} from 'react'
import ReactApexChart from 'react-apexcharts'
import {
  api,
  AnalyticsSummary,
  RevenueBookingsPoint,
  SignupMethodPoint,
  CategorySlice,
  TopEvent,
  ProviderPerformance,
} from '../../services/api'

const CARD: React.CSSProperties = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
}

const fmtMoney = (n: number) => `$${(n || 0).toLocaleString()}`
const fmtNum = (n: number) => (n || 0).toLocaleString()

// ── Growth pill ──────────────────────────────────────────────────────────────
const GrowthPill: FC<{value: number}> = ({value}) => {
  const v = value || 0
  const up = v >= 0
  const txt = `${up ? '+' : ''}${v.toFixed(1)}%`
  return (
    <span style={{display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 13, fontWeight: 700, color: up ? '#22c55e' : '#ef4444'}}>
      {up ? '↑' : '↓'} {txt}
    </span>
  )
}

// ── KPI icons ────────────────────────────────────────────────────────────────
const moneyIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <line x1='12' y1='1' x2='12' y2='23' />
    <path d='M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6' />
  </svg>
)
const usersIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <path d='M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2' />
    <circle cx='9' cy='7' r='4' />
    <path d='M23 21v-2a4 4 0 0 0-3-3.87' />
    <path d='M16 3.13a4 4 0 0 1 0 7.75' />
  </svg>
)
const eventIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <rect x='3' y='4' width='18' height='18' rx='2' />
    <line x1='16' y1='2' x2='16' y2='6' />
    <line x1='8' y1='2' x2='8' y2='6' />
    <line x1='3' y1='10' x2='21' y2='10' />
  </svg>
)
const trendIcon = (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
    <polyline points='22 7 13.5 15.5 8.5 10.5 2 17' />
    <polyline points='16 7 22 7 22 13' />
  </svg>
)

interface KpiDef {
  label: string
  value: string
  delta: number
  bg: string
  iconColor: string
  icon: ReactNode
}

function buildKpis(s: AnalyticsSummary): KpiDef[] {
  return [
    {label: 'Total Revenue', value: fmtMoney(s.totalRevenue), delta: s.revenueGrowth, bg: '#f0fdf4', iconColor: '#22c55e', icon: moneyIcon},
    {label: 'Total Users', value: fmtNum(s.totalUsers), delta: s.usersGrowth, bg: '#eff6ff', iconColor: '#3b82f6', icon: usersIcon},
    {label: 'Total Events', value: fmtNum(s.totalEvents), delta: s.eventsGrowth, bg: '#faf5ff', iconColor: '#a855f7', icon: eventIcon},
    {label: 'Avg. Revenue/Event', value: fmtMoney(s.avgRevenuePerEvent), delta: s.avgRevenueGrowth, bg: '#fff7ed', iconColor: '#f97316', icon: trendIcon},
  ]
}

// ── Chart options ────────────────────────────────────────────────────────────
const revBookingsOptions = (cats: string[]): ApexCharts.ApexOptions => ({
  chart: {type: 'line', toolbar: {show: false}, fontFamily: 'inherit'},
  stroke: {curve: 'smooth', width: 3},
  colors: ['#3b82f6', '#22c55e'],
  dataLabels: {enabled: false},
  markers: {size: 0, hover: {size: 5}},
  xaxis: {categories: cats, axisBorder: {show: false}, axisTicks: {show: false}},
  yaxis: [
    {
      seriesName: 'Revenue ($)',
      labels: {formatter: (v) => `$${Math.round(v).toLocaleString()}`, style: {colors: '#3b82f6'}},
      title: {text: 'Revenue ($)', style: {color: '#3b82f6', fontWeight: 600}},
    },
    {
      seriesName: 'Bookings',
      opposite: true,
      labels: {formatter: (v) => `${Math.round(v).toLocaleString()}`, style: {colors: '#22c55e'}},
      title: {text: 'Bookings', style: {color: '#22c55e', fontWeight: 600}},
    },
  ],
  grid: {borderColor: '#f1f5f9', strokeDashArray: 4},
  legend: {position: 'bottom', horizontalAlign: 'center'},
  tooltip: {shared: true, intersect: false},
})

const signupsOptions = (cats: string[]): ApexCharts.ApexOptions => ({
  chart: {type: 'bar', toolbar: {show: false}, fontFamily: 'inherit', stacked: false},
  plotOptions: {bar: {horizontal: false, columnWidth: '60%', borderRadius: 4}},
  colors: ['#3b82f6', '#22c55e', '#f97316'],
  dataLabels: {enabled: false},
  xaxis: {categories: cats, axisBorder: {show: false}, axisTicks: {show: false}},
  yaxis: {labels: {formatter: (v) => `${Math.round(v).toLocaleString()}`}},
  grid: {borderColor: '#f1f5f9', strokeDashArray: 4},
  legend: {position: 'bottom', horizontalAlign: 'center'},
})

const categoryOptions = (labels: string[]): ApexCharts.ApexOptions => ({
  chart: {type: 'pie', toolbar: {show: false}, fontFamily: 'inherit'},
  labels,
  colors: ['#3b82f6', '#22c55e', '#f97316', '#a855f7', '#06b6d4', '#ec4899', '#eab308', '#ef4444'],
  legend: {position: 'bottom', horizontalAlign: 'center'},
  dataLabels: {enabled: true, formatter: (val) => `${Number(val).toFixed(0)}%`},
  stroke: {width: 2, colors: ['#fff']},
})

// ── Reusable card pieces ─────────────────────────────────────────────────────
const ChartShell: FC<{title: string; subtitle?: string; children: ReactNode}> = ({title, subtitle, children}) => (
  <div style={{...CARD, padding: '20px 20px'}}>
    <div style={{marginBottom: 16}}>
      <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0}}>{title}</h3>
      {subtitle ? <p style={{fontSize: 13, color: '#94a3b8', margin: '4px 0 0'}}>{subtitle}</p> : null}
    </div>
    {children}
  </div>
)

const ChartLoading: FC<{height: number}> = ({height}) => (
  <div style={{height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13}}>
    <span className='spinner-border spinner-border-sm me-2' /> Loading…
  </div>
)

const EmptyState: FC<{height: number; text: string}> = ({height, text}) => (
  <div style={{height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13}}>
    {text}
  </div>
)

const RankBadge: FC<{n: number; color: string; bg: string}> = ({n, color, bg}) => (
  <div style={{width: 28, height: 28, borderRadius: '50%', background: bg, color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0}}>
    {n}
  </div>
)

const AnalyticsPage: FC = () => {
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null)
  const [revBookings, setRevBookings] = useState<RevenueBookingsPoint[]>([])
  const [signups, setSignups] = useState<SignupMethodPoint[]>([])
  const [categories, setCategories] = useState<CategorySlice[]>([])
  const [topEvents, setTopEvents] = useState<TopEvent[]>([])
  const [providers, setProviders] = useState<ProviderPerformance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    let alive = true
    setLoading(true)
    setError(null)
    Promise.all([
      api.getAnalyticsSummary(),
      api.getRevenueBookings('12m'),
      api.getSignupsByMethod('12m'),
      api.getEventsByCategory(),
      api.getTopEvents(5),
      api.getProviderPerformance(5),
    ])
      .then(([s, rb, su, cat, te, pp]) => {
        if (!alive) return
        setSummary(s)
        setRevBookings(rb.points)
        setSignups(su.points)
        setCategories(cat)
        setTopEvents(te)
        setProviders(pp)
      })
      .catch(() => alive && setError('Failed to load analytics. Please try again.'))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }

  useEffect(load, [])

  if (error) {
    return (
      <div style={{...CARD, padding: 48, textAlign: 'center'}}>
        <div style={{color: '#ef4444', fontSize: 15, fontWeight: 600, marginBottom: 12}}>{error}</div>
        <button className='btn btn-sm btn-primary' onClick={load}>
          Retry
        </button>
      </div>
    )
  }

  const kpis = summary ? buildKpis(summary) : []

  const rbCats = revBookings.map((p) => p.label)
  const rbSeries = [
    {name: 'Revenue ($)', type: 'line', data: revBookings.map((p) => p.revenue)},
    {name: 'Bookings', type: 'line', data: revBookings.map((p) => p.bookings)},
  ]

  const suCats = signups.map((p) => p.label)
  const suSeries = [
    {name: 'Email', data: signups.map((p) => p.email)},
    {name: 'Phone', data: signups.map((p) => p.phone)},
    {name: 'Social', data: signups.map((p) => p.social)},
  ]

  const catLabels = categories.map((c) => c.category)
  const catSeries = categories.map((c) => c.count)

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom: 28}}>
        <h1 style={{fontSize: 24, fontWeight: 700, color: '#0f172a', margin: '0 0 4px'}}>Analytics</h1>
        <p style={{fontSize: 14, color: '#64748b', margin: 0}}>Platform performance and insights</p>
      </div>

      {/* KPI cards */}
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 20}}>
        {loading || !summary
          ? [0, 1, 2, 3].map((i) => (
              <div key={i} style={{...CARD, padding: '20px 20px'}}>
                <div style={{height: 96, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13}}>
                  <span className='spinner-border spinner-border-sm' />
                </div>
              </div>
            ))
          : kpis.map((k) => (
              <div key={k.label} style={{...CARD, padding: '20px 20px'}}>
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16}}>
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 10,
                      background: k.bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: k.iconColor,
                      flexShrink: 0,
                    }}
                  >
                    {k.icon}
                  </div>
                  <GrowthPill value={k.delta} />
                </div>
                <div style={{fontSize: 26, fontWeight: 700, color: '#0f172a', marginBottom: 4}}>{k.value}</div>
                <div style={{fontSize: 13, color: '#94a3b8'}}>{k.label}</div>
              </div>
            ))}
      </div>

      {/* Revenue & Bookings Trend */}
      <div style={{marginBottom: 20}}>
        <ChartShell title='Revenue & Bookings Trend'>
          {loading ? (
            <ChartLoading height={340} />
          ) : revBookings.length > 0 ? (
            <ReactApexChart type='line' series={rbSeries} options={revBookingsOptions(rbCats)} height={340} />
          ) : (
            <EmptyState height={340} text='No trend data' />
          )}
        </ChartShell>
      </div>

      {/* Signups by Method + Events by Category */}
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20}}>
        <ChartShell title='Signups by Method' subtitle='New users by signup method'>
          {loading ? (
            <ChartLoading height={300} />
          ) : signups.length > 0 ? (
            <ReactApexChart type='bar' series={suSeries} options={signupsOptions(suCats)} height={300} />
          ) : (
            <EmptyState height={300} text='No signup data' />
          )}
        </ChartShell>

        <ChartShell title='Events by Category'>
          {loading ? (
            <ChartLoading height={300} />
          ) : catSeries.length > 0 ? (
            <ReactApexChart type='pie' series={catSeries} options={categoryOptions(catLabels)} height={300} />
          ) : (
            <EmptyState height={300} text='No category data' />
          )}
        </ChartShell>
      </div>

      {/* Popular Events + Top Service Providers */}
      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20}}>
        {/* Popular Events */}
        <div style={{...CARD, overflow: 'hidden'}}>
          <div style={{padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9'}}>
            <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0}}>Popular Events</h3>
          </div>
          {loading ? (
            <ChartLoading height={200} />
          ) : topEvents.length === 0 ? (
            <div style={{padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13}}>No events yet</div>
          ) : (
            topEvents.map((e, i) => (
              <div
                key={e.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 20px',
                  borderBottom: i < topEvents.length - 1 ? '1px solid #f1f5f9' : 'none',
                }}
              >
                <div style={{display: 'flex', alignItems: 'center', gap: 12, minWidth: 0}}>
                  <RankBadge n={i + 1} color='#3b82f6' bg='#eff6ff' />
                  <div style={{minWidth: 0}}>
                    <div style={{fontSize: 14, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{e.name}</div>
                    <div style={{fontSize: 12, color: '#94a3b8'}}>{e.category}</div>
                  </div>
                </div>
                <div style={{textAlign: 'right', flexShrink: 0, paddingLeft: 12}}>
                  <div style={{fontSize: 14, fontWeight: 700, color: '#0f172a'}}>{fmtMoney(e.revenue)}</div>
                  <div style={{fontSize: 12, color: '#94a3b8'}}>{fmtNum(e.ticketsSold)} attendees</div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Top Service Providers */}
        <div style={{...CARD, overflow: 'hidden'}}>
          <div style={{padding: '20px 20px 16px', borderBottom: '1px solid #f1f5f9'}}>
            <h3 style={{fontSize: 15, fontWeight: 600, color: '#0f172a', margin: 0}}>Top Service Providers</h3>
          </div>
          {loading ? (
            <ChartLoading height={200} />
          ) : providers.length === 0 ? (
            <div style={{padding: '24px 20px', textAlign: 'center', color: '#94a3b8', fontSize: 13}}>No providers yet</div>
          ) : (
            providers.map((p, i) => (
              <div
                key={p.providerId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '14px 20px',
                  borderBottom: i < providers.length - 1 ? '1px solid #f1f5f9' : 'none',
                }}
              >
                <div style={{display: 'flex', alignItems: 'center', gap: 12, minWidth: 0}}>
                  <RankBadge n={i + 1} color='#a855f7' bg='#faf5ff' />
                  <div style={{minWidth: 0}}>
                    <div style={{fontSize: 14, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{p.providerName}</div>
                    <div style={{fontSize: 12, color: '#94a3b8'}}>{p.category}</div>
                  </div>
                </div>
                <div style={{textAlign: 'right', flexShrink: 0, paddingLeft: 12}}>
                  <div style={{fontSize: 14, fontWeight: 700, color: '#0f172a'}}>{fmtMoney(p.revenue)}</div>
                  <div style={{fontSize: 12, color: '#94a3b8'}}>
                    {fmtNum(p.bookings)} events
                    {p.rating && p.rating > 0 ? <span style={{marginLeft: 6, color: '#f59e0b'}}>★ {p.rating.toFixed(1)}</span> : null}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

const AnalyticsPageWrapper: FC = () => <AnalyticsPage />

export {AnalyticsPageWrapper}
